import { ref } from 'vue';
import { defineStore } from 'pinia';
import initializeCognito from '@/utils/cognitoConfig';
import { logError } from '@/utils/logger';
import { SignatureV4 } from '@aws-sdk/signature-v4';
import { Sha256 } from '@aws-crypto/sha256-browser'; // Browser-compatible SHA256

// Store the promise for the cognito identity provider to ensure singleton pattern
let cognitoIdentityProviderPromise = null;

export const useAwsStore = defineStore('aws', () => {
  const awsCredentialProvider = ref(null);
  const signer = ref(null);
  const isInitialized = ref(false);
  const initializationError = ref(null);

  /**
   * Initialize AWS Cognito credentials and signer
   * This function ensures that the credentials and signer are only initialized once
   * even if called multiple times
   */
  const initializeAwsCredentials = async (identityPoolId, region = 'us-east-1') => {
    // If already initialized, return the existing provider
    if (isInitialized.value && awsCredentialProvider.value) {
      return awsCredentialProvider.value;
    }

    // If there was an error in a previous attempt, return the error
    if (initializationError.value) {
      throw new Error(initializationError.value);
    }

    // If initialization is in progress, return the existing promise
    if (cognitoIdentityProviderPromise) {
      return cognitoIdentityProviderPromise;
    }

    // Start initialization process - create the promise immediately to prevent race conditions
    const initializationPromise = (async () => {
      try {
        const provider = await initializeCognito(identityPoolId);
        awsCredentialProvider.value = provider;

        // Initialize the signer with the credentials
        signer.value = new SignatureV4({
          service: 'execute-api', // Service name for API Gateway
          region: region,
          credentials: provider, // Use the provider
          sha256: Sha256,
        });

        isInitialized.value = true;
        initializationError.value = null;
        return provider;
      } catch (error) {
        logError('Error initializing AWS Cognito credentials:', error);
        initializationError.value = error.message;
        throw error;
      } finally {
        // Clear the promise reference when done
        cognitoIdentityProviderPromise = null;
      }
    })();

    // Set the module-level promise immediately to prevent race conditions
    cognitoIdentityProviderPromise = initializationPromise;
    return initializationPromise;
  };

  /**
   * Get the AWS credential provider
   * This will be null if initializeAwsCredentials hasn't been called successfully
   */
  const getCredentialProvider = () => {
    return awsCredentialProvider.value;
  };

  /**
   * Get the AWS signer
   * This will be null if initializeAwsCredentials hasn't been called successfully
   */
  const getSigner = () => {
    return signer.value;
  };

  /**
   * Reset the store state
   * This is useful for testing or when credentials need to be refreshed
   */
  const blankAws = () => {
    awsCredentialProvider.value = null;
    signer.value = null;
    isInitialized.value = false;
    initializationError.value = null;
    cognitoIdentityProviderPromise = null;
  };

  return {
    awsCredentialProvider,
    signer,
    isInitialized,
    initializationError,
    initializeAwsCredentials,
    getCredentialProvider,
    getSigner,
    blankAws,
  };
});
